/** Must match `requestIdFor` in packages/bootstrap/src/request-id.ts, which
 *  decides the id server-side — a stricter rule here would silently drop a
 *  reference the API had already accepted and logged. */
const SAFE_REQUEST_ID = /^[A-Za-z0-9._:-]{1,128}$/;

/** What a user sees for a 5xx whose envelope does not carry `public: true`. */
export const GENERIC_SERVER_ERROR_MESSAGE =
  "Something went wrong on our side. Please try again, or contact support if it keeps happening.";

const SHORT_REFERENCE_LENGTH = 8;

/** A short plain token. Anything else is client text headed for log lines
 *  and a response header, so callers drop it rather than clean it up. */
export function isSafeRequestId(value: unknown): value is string {
  return typeof value === "string" && SAFE_REQUEST_ID.test(value);
}

/** The request id of an API response: the envelope's `error.requestId`
 *  first, else the `X-Request-Id` header. An unsafe value counts as absent. */
export function readRequestId(
  body: unknown,
  headers?: { get(name: string): string | null } | null,
): string | undefined {
  const fromBody = (body as { error?: { requestId?: unknown } } | null)?.error
    ?.requestId;
  if (isSafeRequestId(fromBody)) return fromBody;
  const fromHeader = headers?.get("x-request-id");
  return isSafeRequestId(fromHeader) ? fromHeader : undefined;
}

/** The error message of a response that marked it fit to show a user —
 *  `sendErrorEnvelope` sets `public` only for a 5xx it was given fixed copy
 *  for (packages/bootstrap/src/http/error-envelope.ts). Absent otherwise, so
 *  a proxy or an older API can never opt itself in. */
export function readPublicMessage(body: unknown): string | undefined {
  const error = (body as { error?: { public?: unknown; message?: unknown } })
    ?.error;
  if (error?.public !== true || typeof error.message !== "string")
    return undefined;
  const message = error.message.trim();
  return message.length > 0 ? message : undefined;
}

/** The prefix a user quotes to support; support searches logs by it. */
export function shortReference(id: string): string {
  return id.slice(0, SHORT_REFERENCE_LENGTH);
}
